<template>
	<div class="modal-container modal-xxx-large modal-document-preview">
		<div class="modal-header">
			<span>Finance Channel Communications</span>
		</div>
		<div class="modal-body">
			<div v-if="loading">
				<IsBusySectionComponent />
			</div>
			<div
				v-else-if="creditApplicationsResponses.length === 0"
				class="no-data"
			>
				<i class="fas fa-info-circle"></i> No credit decisions available
			</div>
			<div
				v-else
				class="finance-messages-container"
			>
				<div
					v-for="(response, index) in creditApplicationsResponses"
					:key="index"
					class="credit-decision-card"
				>
					<div class="card-header">
						<div class="status">
							<span class="value">{{ getCreditDecisionStatusInfo(response.status)?.name }}</span>
							<i
								v-if="response.status"
								:class="getCreditDecisionStatusInfo(response.status)?.icon + ' ' + getCreditDecisionStatusInfo(response.status)?.cssClass"
							></i>
						</div>
						<div class="finance-type">{{ response.financeType || 'N/A' }}</div>
					</div>

					<div class="card-content">
						<!-- Left column - Metadata and Analyst Info -->
						<div class="left-column">
							<div class="decision-metadata">
								<div class="metadata-item">
									<span class="label">Application #:</span>
									<span class="value">{{ response.creditDecision.applicationNumber }}</span>
								</div>
								<div class="metadata-item">
									<span class="label">Submitted:</span>
									<span class="value">{{ formatDateTime(response.creditDecision.decision?.submittedDateTime) }}</span>
								</div>
								<div class="metadata-item">
									<span class="label">Decision:</span>
									<span class="value">{{ formatDateTime(response.creditDecision.decision?.decisionDateTime) }}</span>
								</div>
								<div
									class="metadata-item"
									v-if="global.isAdminView"
								>
									<span class="label">Conversation ID:</span>
									<span class="value">{{ response.creditDecision.conversationId }}</span>
								</div>
								<div
									class="metadata-item"
									v-if="global.isAdminView"
								>
									<span class="label">Sequence #:</span>
									<span class="value">{{ response.creditDecision.sequenceNumber }}</span>
								</div>
							</div>

							<div
								class="analyst-section"
								v-if="response.creditDecision.decision"
							>
								<h4>Analyst Information</h4>
								<div class="analyst-details">
									<div class="analyst-item">
										<span class="label">Analyst:</span>
										<span class="value">{{ response.creditDecision.decision.creditAnalystInitials || 'N/A' }}</span>
									</div>
									<div
										class="analyst-item"
										v-if="
											response.creditDecision.creditContractData?.financeCompany?.contact &&
											response.creditDecision.creditContractData.financeCompany.contact.length > 0
										"
									>
										<span class="label">Contact:</span>
										<span class="value">
											{{ response.creditDecision.creditContractData.financeCompany.contact[0].personName || 'N/A' }}
											<span
												v-if="
													response.creditDecision.creditContractData.financeCompany.contact[0].telephone &&
													response.creditDecision.creditContractData.financeCompany.contact[0].telephone.length > 0
												"
											>
												({{ response.creditDecision.creditContractData.financeCompany.contact[0].telephone[0].number }})
											</span>
										</span>
									</div>
									<div class="analyst-item">
										<span class="label">Comments:</span>
										<span
											class="value"
											v-html="response.creditDecision.decision.creditAnalystComments || 'No comments'"
										></span>
									</div>
								</div>
							</div>
						</div>

						<!-- Right column - Decision Comments and Stipulations -->
						<div class="right-column">
							<div class="scrollable-area-container">
								<!-- Decision Comments Section -->
								<div
									class="comments-section"
									v-if="response.creditDecision.decision?.decisionComments && response.creditDecision.decision.decisionComments.length > 0"
								>
									<h5>Decision Comments</h5>
									<div class="comment-list">
										<div
											v-for="(comment, cIndex) in response.creditDecision.decision.decisionComments"
											:key="cIndex"
											class="comment-item"
										>
											<i class="fas fa-comment"></i> <span v-html="R1_CleanDecisionComments(comment)"></span>
										</div>
									</div>
								</div>

								<!-- Condition Rejection Section -->
								<div
									class="condition-rejection-section"
									v-if="response.creditDecision.decision?.conditionRejection && response.creditDecision.decision.conditionRejection.length > 0"
								>
									<h5>Condition Rejection</h5>
									<div class="condition-list">
										<div
											v-for="(condition, rIndex) in response.creditDecision.decision.conditionRejection"
											:key="rIndex"
											class="condition-item"
										>
											<i class="fas fa-exclamation-triangle"></i> <span v-html="R1_CleanDecisionComments(condition?.conditionRejectionText)"></span>
										</div>
									</div>
								</div>

								<!-- Stipulations Section -->
								<div
									class="stipulations-section"
									v-if="response.creditDecision.decision?.stipulations && response.creditDecision.decision.stipulations.length > 0"
								>
									<h5>Stipulations</h5>
									<div class="stipulation-list">
										<div
											v-for="(stipulation, sIndex) in response.creditDecision.decision.stipulations"
											:key="sIndex"
											class="stipulation-item"
										>
											<i class="fas fa-check-circle"></i> <span v-html="R1_CleanDecisionComments(stipulation)"></span>
										</div>
									</div>
								</div>

								<!-- Empty state if no comments, condition rejection, or stipulations -->
								<div
									v-if="
										(!response.creditDecision.decision?.decisionComments || response.creditDecision.decision.decisionComments.length === 0) &&
										(!response.creditDecision.decision?.conditionRejection || response.creditDecision.decision.conditionRejection.length === 0) &&
										(!response.creditDecision.decision?.stipulations || response.creditDecision.decision.stipulations.length === 0)
									"
									class="no-data-section"
								>
									<i class="fas fa-info-circle"></i> No comments or stipulations available
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-footer">
			<button
				class="button-unwind"
				@click="cancel()"
			>
				Close
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
	import $modal from '@core/services/modal';
	import { ref, onMounted, watch, inject } from 'vue';
	import FIMenuCreditApplication from '@core/classes/FIMenuCreditApplication';
	import api from '@core/services/api/_api';
	import util from '@core/services/util';
	import {
		R1_CleanDecisionComments,
		getCreditDecisionStatusInfo,
		getStatusDescriptionFromCode,
		getFinanceTypeDescriptionFromCode,
		CREDIT_DECISION_STATUS_CODES,
		FINANCE_TYPES,
	} from '@core/helpers/finance-channels-helper';
	import IsBusySectionComponent from '@core/components/IsBusySectionComponent.vue';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import { CreditApplicationDecision } from '@core/classes/CreditApplication';

	interface CreditAppResponse {
		creditDecision: any;
		status: CreditApplicationDecision | undefined;
		financeType: string | null;
	}

	const global = inject<SaturnGlobalPlugin>('$global');

	// Props
	const props = defineProps<{
		modal: {
			passedData: {
				row: any;
				fimenu: any;
			};
		};
	}>();

	// Reactive state
	const creditApplicationsResponses = ref<CreditAppResponse[]>([]);

	// Methods
	const formatDateTime = (dateTimeStr: string | undefined): string => {
		if (!dateTimeStr) return 'N/A';
		return util.toMoment(dateTimeStr).format('MM/DD/YYYY h:mm A');
	};

	const cancel = () => {
		$modal.close();
	};

	const handleFetchPartnerId = async (lenderCode: string): Promise<string | null> => {
		try {
			if (!props.modal.passedData?.fimenu) {
				console.error('Missing fimenu data');
				return null;
			}
			if (!lenderCode) return null;
			const response = await api.lenders.getLenderPartnerId(props.modal.passedData.fimenu, lenderCode);
			return response.data.payload;
		} catch (ex) {
			console.error(ex);
			return null;
		}
	};

	const updateContactsForDisplay = (contact: any): void => {
		let telephoneDisplay = contact.telephone?.map((t: any) => {
			return { key: t.type, divider: '\u00A0', value: t.number };
		});
		if (telephoneDisplay) contact.telephoneDisplay = { itemList: telephoneDisplay };

		const emailAddressDisplay = contact.emailAddress?.map((t: string) => {
			return { value: t };
		});
		if (emailAddressDisplay) contact.emailAddressDisplay = { itemList: emailAddressDisplay };

		const faxDisplay = contact.fax?.map((t: string) => {
			return { value: t };
		});
		if (faxDisplay) contact.faxDisplay = { itemList: faxDisplay };
	};

	const loading = ref(false);

	const getCreditDecision = async (): Promise<void> => {
		if (!props.modal.passedData?.row) {
			return;
		}

		try {
			loading.value = true;
			const creditApplication = new FIMenuCreditApplication(props.modal.passedData.row);

			if (!creditApplication.lender?.lenderCode) {
				console.error('Missing lender code');
				return;
			}

			// Find the R1 lender code for this credit application.
			const r1LenderCode = await handleFetchPartnerId(creditApplication.lender.lenderCode);

			if (!r1LenderCode || !creditApplication.conversationId) {
				console.error('Missing required parameters');
				return;
			}

			const response = await api.routeOne.getAllCreditDecisionsForCreditApp(creditApplication.conversationId, r1LenderCode);
			if (response?.data && Array.isArray(response.data)) {
				creditApplicationsResponses.value = response.data.map(resp => {
					const cAppResponse: CreditAppResponse = {
						creditDecision: util.clone(resp),
						status: getStatusDescriptionFromCode(resp.applicationStatus),
						financeType: null,
					};

					if (cAppResponse.creditDecision.decision?.financing) {
						cAppResponse.financeType = getFinanceTypeDescriptionFromCode(cAppResponse.creditDecision.decision.financing.financeType);
					}

					cAppResponse.creditDecision.creditContractData?.financeCompany?.contact?.forEach((c: any) => updateContactsForDisplay(c));

					return cAppResponse;
				});
			}
		} catch (err) {
			console.error(err);
		} finally {
			loading.value = false;
		}
	};
	// // Watch for changes in creditApplicationsResponses and ensure test data is always present
	// const ensureTestData = () => {
	// 	if (creditApplicationsResponses.value.length > 0 && creditApplicationsResponses.value[0].creditDecision.decision) {
	// 		// If stipulations are empty, add test ones
	// 		if (
	// 			!creditApplicationsResponses.value[0].creditDecision.decision.stipulations ||
	// 			creditApplicationsResponses.value[0].creditDecision.decision.stipulations.length === 0
	// 		) {
	// 			creditApplicationsResponses.value[0].creditDecision.decision.stipulations = ['Test Stipulation 1', 'Test Stipulation 2', 'Test Stipulation 3'];
	// 		}

	// 		// If conditionRejection is empty, add test ones
	// 		if (
	// 			!creditApplicationsResponses.value[0].creditDecision.decision.conditionRejection ||
	// 			creditApplicationsResponses.value[0].creditDecision.decision.conditionRejection.length === 0
	// 		) {
	// 			creditApplicationsResponses.value[0].creditDecision.decision.conditionRejection = [
	// 				{ conditionRejectionText: 'Test Condition Rejection 1' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 2' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 				{ conditionRejectionText: 'Test Condition Rejection 3' },
	// 			];
	// 		}
	// 	}
	// };

	// // Watch for changes in the responses array
	// watch(creditApplicationsResponses, ensureTestData, { immediate: true, deep: true });

	// Initialize on component mount
	onMounted(async () => {
		try {
			await getCreditDecision();
		} catch (error) {
			console.error('Error in onMounted:', error);
		}
	});
</script>

<style>
	.finance-messages-container {
		display: flex;
		flex-direction: column;
		padding: 15px;
		overflow-y: auto;
		box-sizing: border-box;
		height: calc(100% - 10px);
	}

	.finance-messages-container * {
		box-sizing: border-box;
	}

	.credit-decision-card {
		display: flex;
		flex-direction: column;
		background-color: #fff;
		border-radius: 6px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		box-sizing: border-box;
		padding: 16px;
		height: 100%;
		margin-bottom: 10px;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-bottom: 10px;
		border-bottom: 1px solid #eee;
	}

	/* Two column layout */
	.card-content {
		display: flex;
		flex-direction: row;
		flex-grow: 1;
		box-sizing: border-box;
		justify-content: space-between;
		gap: 10px;
		height: calc(100% - 30px);
	}

	.left-column {
		flex: 0 0 50%;
		border-right: 1px solid #eee;
		padding-top: 10px;
	}

	.right-column {
		flex: 0 0 50%;
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
		padding-left: 10px;
	}

	/* Left column styling */
	.decision-metadata {
		margin-bottom: 15px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.metadata-item {
		margin-bottom: 5px;
	}

	.analyst-section {
		margin-bottom: 15px;
	}

	.analyst-details {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.analyst-item {
		margin-bottom: 5px;
	}

	/* Right column styling */
	.scrollable-area-container {
		height: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		border-radius: 4px;
		padding: 10px;
		margin-bottom: 15px;
	}

	.comments-section,
	.stipulations-section,
	.condition-rejection-section {
		margin-bottom: 15px;
		width: 100%;
	}

	h5 {
		margin: 5px 0 10px;
		color: #333;
		padding-bottom: 5px;
		border-bottom: 1px solid #eee;
		font-size: 14px;
	}

	/* Remove the individual scrollable areas */
	.scrollable-area {
		display: none;
	}

	/* Replace status badge with status text and icon */
	.status {
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 1.1rem;
		font-weight: 500;
	}

	/* Define icon colors */
	.icon-error {
		color: #dc3545; /* Red for error */
	}

	.icon-approved {
		color: #28a745; /* Green for approved */
	}

	.icon-denied {
		color: #dc3545; /* Red for denied */
	}

	.icon-conditional {
		color: #ffc107; /* Yellow for conditional */
	}

	.icon-preliminary {
		color: #17a2b8; /* Blue for preliminary */
	}

	.icon-withdrawn {
		color: #6c757d; /* Gray for withdrawn */
	}

	.icon-new {
		color: #007bff; /* Blue for new */
	}

	.icon-waiting {
		color: #dc3545; /* Red for waiting */
	}

	.finance-type {
		font-weight: 600;
		color: #666;
	}

	.label {
		color: #666;
		font-size: 0.9rem;
		margin-right: 5px;
	}

	.value {
		font-weight: 500;
	}

	h4 {
		margin: 15px 0 10px;
		color: #333;
		border-bottom: 1px solid #eee;
		padding-bottom: 5px;
	}

	.no-data,
	.no-data-section {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		color: #666;
		font-style: italic;
	}

	.no-data i,
	.no-data-section i {
		margin-right: 10px;
		font-size: 1.2rem;
	}

	.no-data {
		height: 200px;
	}

	.no-data-section {
		height: 150px;
		background-color: #f8f9fa;
		border-radius: 4px;
	}

	.comment-list,
	.stipulation-list,
	.condition-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
	}

	.comment-item,
	.stipulation-item,
	.condition-item {
		background-color: #f8f9fa;
		padding: 8px 12px;
		border-radius: 4px;
		margin-bottom: 8px;
		width: 100%;
		word-break: break-word;
		white-space: normal;
	}

	.comment-item {
		border-left: 3px solid #007bff;
	}

	.stipulation-item {
		border-left: 3px solid #28a745;
	}

	.condition-item {
		border-left: 3px solid #ffc107;
	}

	.comment-item i {
		color: #007bff;
		margin-right: 8px;
	}

	.stipulation-item i {
		color: #28a745;
		margin-right: 8px;
	}

	.condition-item i {
		color: #ffc107;
		margin-right: 8px;
	}
</style>
